import { useState } from 'react'
import { computeGatekeeperPricing, normalizeStudentCount, OPTION_2_MIN_STUDENTS, PER_ID_OPTION_1, PER_ID_OPTION_2, PricingOptionEnum } from '../../pricing/gatekeeperPricing'

const PRICING_DISCLAIMER = `Prices shown are an estimate for the base package only. The final price may change for several reasons — including your school's location and current product/hardware pricing — and is determined by the Gatekeeper salesperson handling your project. These figures are provided for reference only and are not a formal quotation. Contact us for an exact quote tailored to your school.`

// Whole-peso thousands grouping (ASCII, en-US). Deliberately NOT `formatNumber`
// from @ztechapps/common.utils: that barrel pulls numeral + moment +
// libphonenumber (heavy for a marketing bundle), and numeral breaks under SSR
// minification. Totals are whole pesos, so this local grouping + the ₱ symbol
// is all that's needed.
const groupThousands = (n: number): string => String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
const formatPhp = (amount: number) => `₱${groupThousands(amount)}`

export type PricingCalculatorProps = {
  defaultStudentCount?: number
}

export const PricingCalculator = ({ defaultStudentCount = 500 }: PricingCalculatorProps) => {
  const [studentCountInput, setStudentCountInput] = useState(String(defaultStudentCount))

  const studentCount = normalizeStudentCount(studentCountInput)
  const pricing = studentCount === null ? null : computeGatekeeperPricing(studentCount)

  const option1Recommended = pricing?.recommended === PricingOptionEnum.Option1
  const option2Recommended = pricing?.recommended === PricingOptionEnum.Option2

  return (
    <section id="pricing" className="py-6">
      <div className="container">
        <div className="text-center mb-5">
          <h2 className="mb-2">Pricing calculator</h2>
          <p className="text-muted mb-0">Enter your enrollment to compare packages and see the recommended option.</p>
        </div>

        <div className="row justify-content-center mb-4">
          <div className="col-12 col-md-6 col-lg-4">
            <label htmlFor="pricing-student-count" className="form-label fw-semibold">
              Number of students
            </label>
            <input
              id="pricing-student-count"
              type="number"
              min={0}
              step={1}
              inputMode="numeric"
              className="form-control form-control-lg"
              value={studentCountInput}
              onChange={(event) => setStudentCountInput(event.target.value)}
              placeholder="e.g. 500"
            />
          </div>
        </div>

        {pricing === null ? (
          <p className="text-center text-muted">Enter a student count to see pricing.</p>
        ) : (
          <div className="row g-4 justify-content-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div className={`card h-100 position-relative ${option1Recommended ? 'border-primary' : ''}`}>
                {option1Recommended && <span className="badge text-bg-primary position-absolute top-0 end-0 m-3">Recommended</span>}
                <div className="card-body">
                  <h5 className="card-title">Option 1 — Equipment + Per-ID</h5>
                  <p className="text-muted small mb-3">
                    One-time equipment + {formatPhp(PER_ID_OPTION_1)} per student ID — each ID includes the photo, student &amp; guardian details, and the printed RFID card. No minimum enrollment.
                  </p>
                  <div className="display-6 fw-bold">{formatPhp(pricing.option1Total)}</div>
                  <div className="text-muted small">total for {groupThousands(studentCount)} students</div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6 col-lg-5">
              <div className={`card h-100 position-relative ${option2Recommended ? 'border-primary' : ''}`}>
                {option2Recommended && <span className="badge text-bg-primary position-absolute top-0 end-0 m-3">Recommended</span>}
                <div className="card-body">
                  <h5 className="card-title">Option 2 — All-Inclusive Per-ID</h5>
                  <p className="text-muted small mb-3">
                    {formatPhp(PER_ID_OPTION_2)} per student ID — each ID includes the photo, student &amp; guardian details, and the printed RFID card. Equipment included.
                  </p>
                  {pricing.option2Available && pricing.option2Total !== null ? (
                    <>
                      <div className="display-6 fw-bold">{formatPhp(pricing.option2Total)}</div>
                      <div className="text-muted small">total for {groupThousands(studentCount)} students</div>
                    </>
                  ) : (
                    <div className="text-muted">Requires {OPTION_2_MIN_STUDENTS}+ students.</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <p className="small text-muted mt-4 mb-1">
          Both options include a 3-month equipment warranty. SMS/SIM credits and a stable internet connection are provided by the school and are not part of the price.
        </p>
        <p className="small text-muted mb-0">{PRICING_DISCLAIMER}</p>
      </div>
    </section>
  )
}
